import { useQuery } from "@tanstack/react-query";
import { addDays, differenceInCalendarDays, format, parseISO, startOfToday } from "date-fns";
import { Search, Tag } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useBooking } from "../context/BookingContext";
import { useSitePath } from "../context/SiteContext";
import { Checkbox } from "./ui/checkbox";
import { DateField } from "./DateField";
import { HotelSuggest } from "./HotelSuggest";
import { SelectField, range } from "./SelectField";

export const useLocations = () =>
  useQuery({ queryKey: ["locations"], queryFn: () => api.get("/locations").then((r) => r.data) });

export const SearchPanel = ({ onDone, onSearch, compact = false }) => {
  const { criteria, setCriteria } = useBooking();
  const [form, setForm] = useState(criteria);
  const { data: loc } = useLocations();
  const navigate = useNavigate();
  const sp = useSitePath();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const departure = format(addDays(parseISO(form.arrival), form.nights), "yyyy-MM-dd");
  const cities = (loc?.cities || []).filter((c) => !Number(form.country_id) || c.country_id === Number(form.country_id));
  const only = loc?.hotels?.length === 1 ? loc.hotels[0] : null;
  useEffect(() => {
    if (only && form.hotel_id !== only.id) {
      setForm((f) => ({ ...f, country_id: 0, city_id: 0, hotel_id: only.id, hotel_name: only.name }));
    }
  }, [only, form.hotel_id]);

  const submit = (e) => {
    e.preventDefault();
    if (onSearch) return onSearch(form);
    setCriteria(form);
    onDone?.();
    navigate(sp("/hotels"));
  };

  return (
    <form onSubmit={submit} className={`grid grid-cols-2 gap-4 ${compact ? "" : "md:grid-cols-4 lg:grid-cols-6"}`} data-testid="search-panel">
      {loc && !only && (
        <>
          <SelectField label="Country" value={form.country_id} testid="search-country-select"
            onChange={(v) => set({ country_id: Number(v), city_id: 0 })}
            options={[{ value: 0, label: "Select Country" }, ...(loc?.countries || []).map((c) => ({ value: c.id, label: c.name }))]} />
          <SelectField label="City" value={form.city_id} testid="search-city-select"
            onChange={(v) => set({ city_id: Number(v) })}
            options={[{ value: 0, label: "Select City" }, ...cities.map((c) => ({ value: c.id, label: c.name }))]} />
          <div className="col-span-2">
            <HotelSuggest form={form} onPick={(h) => set({ hotel_id: h?.id || "", hotel_name: h?.name || "" })} />
          </div>
        </>
      )}
      <DateField label="Arrival Date" value={form.arrival} minDate={startOfToday()} testid="search-arrival-date"
        onChange={(arrival) => set({ arrival })} />
      <DateField label="Departure Date" value={departure} minDate={addDays(parseISO(form.arrival), 1)} testid="search-departure-date"
        onChange={(d) => set({ nights: Math.min(30, differenceInCalendarDays(parseISO(d), parseISO(form.arrival))) })} />
      <div className="field">
        <span className="field-label">Nights</span>
        <input type="number" min={1} max={30} className="field-input" value={form.nights} data-testid="search-nights-input"
          onChange={(e) => set({ nights: Math.max(1, Math.min(30, Number(e.target.value) || 1)) })} />
      </div>
      <SelectField label="Rooms" value={form.rooms} options={range(1, 5)} testid="search-rooms-select" onChange={(v) => set({ rooms: Number(v) })} />
      <SelectField label="Adults / room" value={form.adults} options={range(1, 6)} testid="search-adults-select" onChange={(v) => set({ adults: Number(v) })} />
      <SelectField label="Kids / room" value={form.kids} options={range(0, 6)} testid="search-kids-select" onChange={(v) => set({ kids: Number(v) })} />
      <div className="field col-span-2">
        <span className="field-label">Promotion Code</span>
        <div className="relative">
          <Tag className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gold" strokeWidth={1.5} />
          <input className="field-input !pl-9 uppercase" placeholder="Optional" value={form.promo} data-testid="search-promo-input"
            onChange={(e) => set({ promo: e.target.value })} />
        </div>
      </div>
      <div className="col-span-2 flex flex-wrap items-end gap-5 pb-3 md:col-span-2">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-ink/80">
          <Checkbox checked={form.show_packages} data-testid="search-show-packages-checkbox"
            onCheckedChange={(v) => set({ show_packages: !!v, packages_only: v ? form.packages_only : false })} />
          Show Packages
        </label>
        {form.show_packages && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink/80">
            <Checkbox checked={form.packages_only} data-testid="search-packages-only-checkbox" onCheckedChange={(v) => set({ packages_only: !!v })} />
            Packages Only
          </label>
        )}
      </div>
      <div className="col-span-2 flex items-end">
        <button type="submit" className="btn-primary w-full" data-testid="search-check-availability-button">
          <Search className="h-4 w-4" /> Check Availability
        </button>
      </div>
    </form>
  );
};
